// Ngăn kéo xem chi tiết: điện thoại trượt lên từ đáy (như Dialog), từ `sm` trở lên là ngăn
// bên phải cao hết màn hình — xem một danh sách dài mà vẫn thấy trang phía sau.

import type { ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  focusContent = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  /** Mở ra thì đặt focus vào chính ngăn (Tab mới tới ô đầu) — ô đầu là nút chọn (tab) thì viền focus trông như đang chọn */
  focusContent?: boolean
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          {...(focusContent
            ? {
                onOpenAutoFocus: (e: Event) => {
                  e.preventDefault()
                  ;(e.currentTarget as HTMLElement | null)?.focus()
                },
              }
            : {})}
          className={cn(
            'fixed z-50 flex flex-col bg-raise shadow-overlay',
            'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t border-rule pb-[env(safe-area-inset-bottom)]',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
            'sm:inset-y-0 sm:left-auto sm:right-0 sm:h-dvh sm:max-h-dvh sm:w-full sm:max-w-2xl sm:rounded-none sm:border-l sm:border-t-0 sm:pb-0',
            'sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:slide-in-from-right sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:slide-out-to-right',
            // Focus đặt vào chính ngăn (không phải một ô bấm) — không vẽ vòng focus quanh cả ngăn
            focusContent && 'outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
            className,
          )}
        >
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-rule sm:hidden" />
          <div className="shrink-0 border-b border-rule px-4 pb-3 pr-14 pt-3 sm:px-6 sm:pt-5">
            <DialogPrimitive.Title className="text-xl leading-tight tracking-tight text-ink">{title}</DialogPrimitive.Title>
            {description && <DialogPrimitive.Description className="mt-1 text-sm text-inkSoft">{description}</DialogPrimitive.Description>}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
          <DialogPrimitive.Close
            className="absolute right-2 top-3 flex size-11 items-center justify-center rounded-lg text-muted opacity-70 transition-opacity hover:bg-paperSoft hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent sm:top-4 sm:size-9"
            aria-label="Đóng"
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
